import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { CaretLeft, CaretUp, CaretDown, DownloadSimple, ShareNetwork, Check, MagnifyingGlass } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Logo from '../components/Logo';
import MonthPicker, { monthNamesFull } from '../components/MonthPicker';
import { formatMoney } from '../utils/currency';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export default function Report() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [sortBy, setSortBy] = useState('date');
  const [sortDir, setSortDir] = useState('asc');
  const [year, setYear] = useState(parseInt(searchParams.get('year')) || new Date().getFullYear());
  const [month, setMonth] = useState(parseInt(searchParams.get('month')) || new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [sharing, setSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [toast, setToast] = useState(null);

  function showToast(message, type = 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  const monthNames = monthNamesFull;

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses/${year}/${month}`);
      setExpenses(data.expenses);
      setTotal(data.total);
    } catch {
      console.error('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  function handleMonthChange(m, y) {
    setMonth(m);
    setYear(y);
  }

  // Categorias disponiveis para filtro
  const availableCategories = [...new Set(expenses.map(e => e.category || 'Geral'))].sort();

  // Filtrar despesas
  const filteredExpenses = expenses.filter(e => {
    if (search && !e.description.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory && (e.category || 'Geral') !== filterCategory) return false;
    return true;
  });

  const filteredTotal = filteredExpenses.reduce((sum, e) => sum + parseFloat(e.amount), 0);

  function exportCSV() {
    const headers = ['Descricao', 'Valor', 'Categoria', 'Data', 'Recorrencia'];
    const rows = filteredExpenses.map(e => [
      e.description,
      parseFloat(e.amount).toFixed(2),
      e.category,
      new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
      { none: 'Nenhuma', daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[e.recurrence] || 'Nenhuma',
    ]);

    const totalRow = ['Total', filteredTotal.toFixed(2), '', '', ''];
    const csv = [headers, ...rows, totalRow].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `despeasy-${monthNames[month - 1].toLowerCase()}-${year}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  function exportPDF() {
    if (!user?.planLimits?.exportPdf) {
      showToast('Exportar PDF e um recurso do plano Pro', 'upgrade');
      return;
    }
    const doc = new jsPDF();

    // Logo texto: "Desp" preto + "Easy" azul
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(15, 23, 42); // slate-900
    doc.text('Desp', 14, 18);
    const despWidth = doc.getTextWidth('Desp');
    doc.setTextColor(37, 99, 235); // blue-600
    doc.text('Easy', 14 + despWidth, 18);

    // Subtitulo
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(148, 163, 184); // slate-400
    doc.text(`Relatorio - ${monthNames[month - 1]} ${year}`, 14, 24);

    autoTable(doc, {
      startY: 32,
      head: [['Descricao', 'Categoria', 'Data', 'Recorrencia', 'Valor']],
      body: filteredExpenses.map(e => [
        e.description,
        e.category,
        new Date(e.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' }),
        { none: '-', daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[e.recurrence] || '-',
        formatMoney(e.amount, user),
      ]),
      foot: [['Total', '', '', '', formatMoney(filteredTotal, user)]],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [37, 99, 235] },
      footStyles: { fillColor: [241, 245, 249], textColor: [30, 41, 59], fontStyle: 'bold' },
    });

    doc.save(`despeasy-${monthNames[month - 1].toLowerCase()}-${year}.pdf`);
  }

  async function handleShare() {
    setSharing(true);
    setShareUrl('');
    try {
      const { data } = await api.post('/share', { year, month });
      setShareUrl(data.url);
      navigator.clipboard.writeText(data.url).catch(() => {});
    } catch (err) {
      const data = err.response?.data;
      if (data?.upgradeRequired) {
        showToast(data.error, 'upgrade');
      }
      setShareUrl('');
    } finally {
      setSharing(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo linkTo="/dashboard" />
          <Link
            to="/dashboard"
            className="text-sm text-slate-400 hover:text-slate-600 transition flex items-center gap-1.5"
          >
            <CaretLeft size={16} />
            <span className="hidden sm:inline">Voltar ao dashboard</span>
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Header da pagina */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-800">Relatorio</h1>
            <p className="text-slate-400 text-sm mt-1">Visualize e exporte suas despesas</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleShare}
              disabled={expenses.length === 0 || sharing}
              className="border border-slate-200 text-slate-600 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl hover:bg-slate-50 transition text-sm font-medium shadow-sm flex items-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {sharing ? (
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : shareUrl ? (
                <Check size={16} className="text-emerald-500" />
              ) : (
                <ShareNetwork size={16} />
              )}
              <span className="hidden sm:inline">{shareUrl ? 'Copiado!' : 'Compartilhar'}</span>
            </button>
            <button
              onClick={exportPDF}
              disabled={expenses.length === 0}
              className="bg-blue-600 text-white px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition text-sm font-medium shadow-sm flex items-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <DownloadSimple size={16} />
              PDF
            </button>
            <button
              onClick={exportCSV}
              disabled={expenses.length === 0}
              className="border border-slate-200 text-slate-600 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl hover:bg-slate-50 transition text-sm font-medium shadow-sm flex items-center gap-1.5 sm:gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <DownloadSimple size={16} />
              CSV
            </button>
          </div>
        </div>

        {/* Navegacao de mes */}
        <div className="mb-6 flex">
          <MonthPicker month={month} year={year} onChange={handleMonthChange} disabled={loading} />
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20">
            <svg className="w-6 h-6 text-blue-500 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        )}

        <div className={`transition-opacity duration-200 ${loading ? 'hidden' : 'opacity-100'}`}>

        {/* Graficos */}
        {expenses.length > 0 && (() => {
          const cats = {};
          expenses.forEach(e => {
            const cat = e.category || 'Geral';
            cats[cat] = (cats[cat] || 0) + parseFloat(e.amount);
          });
          const sorted = Object.entries(cats).sort(([,a], [,b]) => b - a);
          const colors = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#f97316', '#6366f1'];
          const maxVal = Math.max(...sorted.map(([,v]) => v));

          // Donut SVG
          let offset = 0;
          const slices = sorted.map(([cat, val], i) => {
            const pct = total > 0 ? (val / total) * 100 : 0;
            const slice = { cat, val, pct, color: colors[i % colors.length], offset };
            offset += pct;
            return slice;
          });

          return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
              {/* Donut */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6">
                <h3 className="font-semibold text-slate-800 mb-4">Por categoria</h3>
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <div className="shrink-0">
                    <svg width="120" height="120" viewBox="0 0 42 42" className="sm:w-[140px] sm:h-[140px]">
                      <circle cx="21" cy="21" r="15.91" fill="transparent" stroke="#e2e8f0" strokeWidth="4" />
                      {slices.map((s, i) => (
                        <circle
                          key={i}
                          cx="21" cy="21" r="15.91"
                          fill="transparent"
                          stroke={s.color}
                          strokeWidth="4"
                          strokeDasharray={`${s.pct} ${100 - s.pct}`}
                          strokeDashoffset={`${-s.offset + 25}`}
                          strokeLinecap="round"
                        />
                      ))}
                    </svg>
                  </div>
                  <div className="flex-1 space-y-2">
                    {slices.map((s, i) => (
                      <div key={i} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: s.color }} />
                          <span className="text-xs text-slate-600">{s.cat}</span>
                        </div>
                        <span className="text-xs font-medium text-slate-800">{Math.round(s.pct)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Barras */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 p-6">
                <h3 className="font-semibold text-slate-800 mb-4">Valores por categoria</h3>
                <div className="space-y-3">
                  {sorted.map(([cat, val], i) => (
                    <div key={cat}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-slate-600">{cat}</span>
                        <span className="text-xs font-medium text-slate-800">{formatMoney(val, user)}</span>
                      </div>
                      <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-700"
                          style={{ width: `${(val / maxVal) * 100}%`, backgroundColor: colors[i % colors.length] }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Busca e filtro */}
        {expenses.length > 0 && (
          <div className="flex flex-col sm:flex-row gap-2 mb-6">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} className="text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar despesa..."
                className="w-full pl-9 pr-3 py-2.5 bg-white border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition shadow-sm"
              />
            </div>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition shadow-sm"
            >
              <option value="">Todas categorias</option>
              {availableCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        )}

        {/* Tabela */}
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
            {filteredExpenses.length === 0 ? (
              <div className="p-16 text-center">
                <p className="text-slate-400">{search || filterCategory ? 'Nenhuma despesa encontrada' : 'Nenhuma despesa neste mes'}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-100">
                      {[
                        { key: 'description', label: 'Descricao', align: 'left' },
                        { key: 'category', label: 'Categoria', align: 'left' },
                        { key: 'date', label: 'Data', align: 'left' },
                        { key: 'recurrence', label: 'Recorrencia', align: 'left' },
                        { key: 'amount', label: 'Valor', align: 'right' },
                      ].map(col => (
                        <th
                          key={col.key}
                          onClick={() => {
                            if (sortBy === col.key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                            else { setSortBy(col.key); setSortDir('asc'); }
                          }}
                          className={`text-${col.align} text-xs font-medium text-slate-400 uppercase tracking-wider px-3 sm:px-6 py-3 cursor-pointer hover:text-slate-600 transition select-none whitespace-nowrap`}
                        >
                          <span className="inline-flex items-center gap-1">
                            {col.label}
                            {sortBy === col.key && (
                              sortDir === 'asc' ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[...filteredExpenses].sort((a, b) => {
                      let valA, valB;
                      if (sortBy === 'amount') {
                        valA = parseFloat(a.amount); valB = parseFloat(b.amount);
                      } else if (sortBy === 'date') {
                        valA = new Date(a.date); valB = new Date(b.date);
                      } else {
                        valA = (a[sortBy] || '').toLowerCase(); valB = (b[sortBy] || '').toLowerCase();
                      }
                      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
                      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
                      return 0;
                    }).map((expense, index) => (
                      <tr
                        key={expense.id}
                        className={`hover:bg-slate-50/60 transition ${
                          index < filteredExpenses.length - 1 ? 'border-b border-slate-50' : ''
                        }`}
                      >
                        <td className="px-3 sm:px-6 py-3.5 text-sm font-medium text-slate-800 max-w-[150px] sm:max-w-none truncate">{expense.description}</td>
                        <td className="px-3 sm:px-6 py-3.5">
                          <span className="text-[11px] text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md font-medium whitespace-nowrap">{expense.category}</span>
                        </td>
                        <td className="px-3 sm:px-6 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                          {new Date(expense.date).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                        </td>
                        <td className="px-3 sm:px-6 py-3.5 text-sm text-slate-500 whitespace-nowrap">
                          {{ none: '-', daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[expense.recurrence] || '-'}
                        </td>
                        <td className="px-3 sm:px-6 py-3.5 text-sm font-semibold text-slate-800 text-right whitespace-nowrap">
                          {formatMoney(expense.amount, user)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t border-slate-200 bg-slate-50/50">
                      <td colSpan={4} className="px-3 sm:px-6 py-3.5 text-sm font-semibold text-slate-800">Total</td>
                      <td className="px-3 sm:px-6 py-3.5 text-sm font-bold text-slate-800 text-right whitespace-nowrap">{formatMoney(filteredTotal, user)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium animate-fade-in ${
          toast.type === 'upgrade'
            ? 'bg-amber-500 text-white cursor-pointer'
            : toast.type === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-emerald-500 text-white'
        }`} onClick={toast.type === 'upgrade' ? () => navigate('/account?tab=plans') : undefined}>
          {toast.message}
          {toast.type === 'upgrade' && <span className="ml-2 underline">Ver planos</span>}
        </div>
      )}
    </div>
  );
}
